import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { Calendar, Clock, BookOpen, Star, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { GroupBadges, GroupActions, MeetingLinkButton } from '../components/sessions/BookingGroupPanel';
import JoinRequestsDialog from '../components/sessions/JoinRequestsDialog';
import JoinedSessions from '../components/sessions/JoinedSessions';

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingModal, setRatingModal] = useState(null);
  const [ratingData, setRatingData] = useState({ rating: 5, review: '' });
  const [submitting, setSubmitting] = useState(false);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [requestsFor, setRequestsFor] = useState(null);
  const [joinedCount, setJoinedCount] = useState(0);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const openRatingModal = (booking) => {
    setRatingModal(booking);
    setRatingData({ rating: 5, review: '' });
  };

  const submitRating = async () => {
    if (ratingData.rating < 1 || ratingData.rating > 5) {
      toast.error('Please select a rating between 1 and 5');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${ratingModal.id}/rate`, {
        booking_id: ratingModal.id,
        rating: ratingData.rating,
        review: ratingData.review || null
      });
      toast.success('Thank you for your feedback!');
      setRatingModal(null);
      fetchBookings();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCancel = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/bookings/${cancelModal.id}/cancel-self`, { reason: cancelReason || null });
      toast.success('Your booking has been cancelled.');
      setCancelModal(null);
      setCancelReason('');
      fetchBookings();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = (b) =>
    b.booking_status !== 'cancelled' &&
    b.booking_status !== 'completed' &&
    b.payment_status !== 'rejected';

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      confirmed: 'bg-blue-100 text-blue-700 border-blue-300',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      cancelled: 'bg-red-100 text-red-700 border-red-300'
    };
    return styles[status] || styles.pending;
  };

  const paymentLabel = {
    pending: 'Awaiting payment',
    awaiting_verification: 'Payment under verification',
    paid: 'Payment verified',
    rejected: 'Payment rejected',
  };

  const paymentBadgeStyle = {
    pending: 'bg-slate-100 text-slate-600 border-slate-300',
    awaiting_verification: 'bg-amber-100 text-amber-700 border-amber-300',
    paid: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    rejected: 'bg-red-100 text-red-700 border-red-300',
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: AlertCircle,
      confirmed: CheckCircle2,
      completed: CheckCircle2,
      cancelled: XCircle
    };
    const Icon = icons[status] || AlertCircle;
    return <Icon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Loading your bookings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="my-bookings-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">My Bookings</h1>
        <p className="text-lg text-slate-600">View and manage your teaching sessions</p>
      </div>

      {bookings.length === 0 && joinedCount > 0 ? null : bookings.length === 0 ? (
        <Card className="border-0 shadow-xl">
          <CardContent className="py-16 text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-12 h-12 text-violet-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No Bookings Yet</h2>
            <p className="text-slate-600 mb-6">Book a session with a teacher to get started</p>
            <Link to="/teachers">
              <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8">
                Book Session
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking, index) => (
            <motion.div
              key={booking.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-violet-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{booking.subject}</h3>
                          <p className="text-sm text-slate-500">Booking ID: {booking.id.slice(0, 8)}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(booking.booking_date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Clock className="w-4 h-4" />
                          <span>{booking.start_time} ({booking.duration_hours}h)</span>
                        </div>
                        <div>
                          <Badge className={getStatusBadge(booking.booking_status)}>
                            {getStatusIcon(booking.booking_status)}
                            <span className="ml-1 capitalize">{booking.booking_status}</span>
                          </Badge>
                        </div>
                        <div>
                          <span className="font-semibold text-violet-600">₹{booking.total_amount}</span>
                        </div>
                      </div>

                      {booking.section && (
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Section:</span> {booking.section}
                          {booking.chapter && ` • ${booking.chapter}`}
                        </p>
                      )}

                      <GroupBadges booking={booking} />

                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          className={paymentBadgeStyle[booking.payment_status] || paymentBadgeStyle.pending}
                          data-testid={`booking-payment-status-${booking.id}`}
                        >
                          {paymentLabel[booking.payment_status] || 'Awaiting payment'}
                        </Badge>
                      </div>

                      {booking.payment_status === 'awaiting_verification' && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`booking-processing-note-${booking.id}`}>
                          Your booking is being processed. You'll be notified here and by email once our team verifies the payment.
                        </p>
                      )}
                      {booking.payment_status === 'rejected' && booking.verification_note && (
                        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`booking-rejected-note-${booking.id}`}>
                          {booking.verification_note}
                        </p>
                      )}

                      {booking.booking_status === 'cancelled' && (
                        <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid={`booking-cancelled-note-${booking.id}`}>
                          {booking.refund_status === 'refunded'
                            ? 'This booking was cancelled and your refund has been processed.'
                            : booking.refund_status === 'pending'
                            ? 'This booking was cancelled. Your refund is being processed by our finance team.'
                            : 'This booking has been cancelled.'}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {booking.booking_status !== 'cancelled' && (
                        <MeetingLinkButton link={booking.meeting_link} testId={`meeting-link-${booking.id}`} />
                      )}
                      <GroupActions booking={booking} onOpenRequests={setRequestsFor} />
                      {booking.booking_status === 'completed' && (
                        booking.student_rating ? (
                          <div
                            className="flex items-center gap-1 text-amber-600 font-semibold"
                            data-testid={`booking-rated-${booking.id}`}
                          >
                            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                            Rated {booking.student_rating}/5
                          </div>
                        ) : (
                          <Button
                            onClick={() => openRatingModal(booking)}
                            className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                            data-testid={`rate-booking-${booking.id}`}
                          >
                            <Star className="w-4 h-4 mr-2" />
                            Rate Session
                          </Button>
                        )
                      )}
                      {canCancel(booking) && (
                        <Button
                          onClick={() => { setCancelModal(booking); setCancelReason(''); }}
                          variant="outline"
                          className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                          data-testid={`cancel-booking-${booking.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel Booking
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <JoinedSessions onCount={setJoinedCount} />

      <JoinRequestsDialog booking={requestsFor} onClose={() => setRequestsFor(null)} onChanged={fetchBookings} />

      {/* Rating Modal */}
      <Dialog open={!!ratingModal} onOpenChange={() => setRatingModal(null)}>
        <DialogContent data-testid="rating-modal">
          <DialogHeader>
            <DialogTitle>Rate Your Session</DialogTitle>
            <DialogDescription>Share your experience to help other students</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rating *</Label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRatingData({ ...ratingData, rating: star })}
                    className="focus:outline-none"
                    type="button"
                    data-testid={`star-${star}`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= ratingData.rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-300'
                      } transition-colors cursor-pointer hover:scale-110`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-lg font-semibold">{ratingData.rating}/5</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Review (Optional)</Label>
              <Textarea
                placeholder="Share your experience with this teacher..."
                value={ratingData.review}
                onChange={(e) => setRatingData({ ...ratingData, review: e.target.value })}
                rows={4}
                data-testid="review-textarea"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={submitRating}
                disabled={submitting}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                data-testid="submit-rating-button"
              >
                {submitting ? 'Submitting...' : 'Submit Rating'}
              </Button>
              <Button
                onClick={() => setRatingModal(null)}
                variant="outline"
                className="flex-1 rounded-full"
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Modal */}
      <Dialog open={!!cancelModal} onOpenChange={(v) => { if (!v) setCancelModal(null); }}>
        <DialogContent data-testid="cancel-modal">
          <DialogHeader>
            <DialogTitle>Cancel this booking?</DialogTitle>
            <DialogDescription>
              Your session ({cancelModal?.subject}) will be cancelled. If it was paid, your refund will be processed
              by our finance team. You can only cancel at least 1 hour before the session time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="self-cancel-reason">Reason (optional)</Label>
              <Textarea
                id="self-cancel-reason"
                placeholder="Let us know why you're cancelling..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                data-testid="self-cancel-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={submitCancel}
                disabled={cancelling}
                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full"
                data-testid="confirm-self-cancel-button"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
              <Button
                onClick={() => setCancelModal(null)}
                variant="outline"
                className="flex-1 rounded-full"
                disabled={cancelling}
              >
                Keep Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBookings;